import { useState, useCallback, useRef } from "react";
import { getLessonPlans } from "../../../services/LessonPlanService";
import { handleApiError } from "../../../utils/errorHandler";
import { ALL_PLANS_PER_PAGE } from "../LessonPlannerConstant";
import { normalizeLessonList } from "../utils/lessonPlanUtils";

export const INITIAL_ALL_PLAN_FILTERS = {
  teacher_id: "",
  program_id: "",
  subject_id: "",
  status: "",
  lesson_date_from: "",
  lesson_date_to: "",
  search: "",
  sort_order: "desc",
};

/** Lesson plans of every teacher (needs lesson_plan.view_all). */
export const useAllLessonPlans = () => {
  const [lessons, setLessons] = useState([]);

  // UI filters (typing/selecting only)
  const [filters, setFilters] = useState(INITIAL_ALL_PLAN_FILTERS);
  // Applied filters (API only)
  const appliedFiltersRef = useRef(INITIAL_ALL_PLAN_FILTERS);

  const [isLoading, setIsLoading] = useState(false);
  const [lastPage, setLastPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  // Ignore responses that arrive after a newer request
  const requestRef = useRef(0);

  /* ================= FETCH ================= */
  const fetchLessons = useCallback(async (page = 1, payload) => {
    if (payload) appliedFiltersRef.current = payload;
    const requestId = ++requestRef.current;

    setIsLoading(true);
    try {
      const response = await getLessonPlans({
        ...appliedFiltersRef.current,
        sort_by: "lesson_date",
        page,
        per_page: ALL_PLANS_PER_PAGE,
      });
      if (requestId !== requestRef.current) return;

      const result = normalizeLessonList(response);
      setLessons(result.lessons);
      setLastPage(result.lastPage);
      setTotalRecords(result.totalRecords);
      setCurrentPage(page - 1);
    } catch (error) {
      if (requestId === requestRef.current) handleApiError(error);
    } finally {
      if (requestId === requestRef.current) setIsLoading(false);
    }
  }, []);

  /* ================= SEARCH / RESET ================= */
  const handleSearch = useCallback(() => fetchLessons(1, filters), [filters, fetchLessons]);

  const handleReset = useCallback(() => {
    setFilters(INITIAL_ALL_PLAN_FILTERS);
    fetchLessons(1, INITIAL_ALL_PLAN_FILTERS);
  }, [fetchLessons]);

  /* ================= FILTER CHANGE (NO API) ================= */
  const handleFilterChange = useCallback((event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "program_id" && { subject_id: "" }),
    }));
  }, []);

  /* ================= PAGINATION ================= */
  const handlePageChange = useCallback(
    ({ selected }) => fetchLessons(selected + 1),
    [fetchLessons],
  );

  return {
    lessons,
    filters,
    isLoading,
    lastPage,
    currentPage,
    totalRecords,
    fetchLessons,
    handleFilterChange,
    handleSearch,
    handleReset,
    handlePageChange,
  };
};
